/**
 * Shared AG Grid cell renderers matching the Payment Links design system.
 * Import these into any definitions-config.jsx to get consistent styling.
 */
import React from 'react';
import moment from 'moment';

// ─── Date cell renderer ───────────────────────────────────────────
export function DateCellRenderer(props) {
  const date = props.value;
  return (
    <span style={{ color: '#6B7280', fontSize: 12.5 }}>
      {date ? moment(date).format('DD MMM YYYY, hh:mm a') : '—'}
    </span>
  );
}

// ─── Generic text cell renderer factory ───────────────────────────
export function createTextRenderer(getValue) {
  function TextCell(props) {
    const value = getValue(props);
    return (
      <span style={{ fontSize: 13, color: '#374151' }}>
        {value || '—'}
      </span>
    );
  }
  return TextCell;
}

// ─── Bold text cell renderer factory ──────────────────────────────
export function createBoldTextRenderer(getValue) {
  function BoldTextCell(props) {
    const value = getValue(props);
    return (
      <span style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>
        {value || '—'}
      </span>
    );
  }
  return BoldTextCell;
}

// ─── Status pill cell renderer factory ────────────────────────────
export function createStatusPillRenderer(statusMap) {
  function StatusPillRenderer(props) {
    const status = props.value;
    const style = statusMap[status] || {
      bg: '#F9FAFB',
      color: '#6B7280',
      border: '#E5E7EB',
      label: status || '—',
    };
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 10px',
          borderRadius: '9999px',
          fontSize: 11.5,
          fontWeight: 600,
          backgroundColor: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          lineHeight: 1.6,
        }}
      >
        {style.label}
      </span>
    );
  }
  return StatusPillRenderer;
}

// ─── Avatar + name cell renderer factory ──────────────────────────
export function createAvatarNameRenderer(getFirstName, getLastName) {
  function AvatarNameRenderer(props) {
    const firstName = getFirstName(props) || '';
    const lastName = getLastName(props) || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const initials = fullName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    const colours = ['#EEF2FF', '#E0F2FE', '#D1FAE5', '#FEF3C7', '#FCE7F3'];
    const textColours = ['#4F46E5', '#0369A1', '#047857', '#B45309', '#BE185D'];
    const hash = fullName ? fullName.charCodeAt(0) % colours.length : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: colours[hash],
            color: textColours[hash],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials || '—'}
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
          {fullName || '—'}
        </span>
      </div>
    );
  }
  return AvatarNameRenderer;
}

// ─── Link cell renderer factory ───────────────────────────────────
export function createLinkRenderer(getUrl) {
  function LinkCellRenderer(props) {
    const link = getUrl(props);
    if (!link) return <span style={{ color: '#9CA3AF' }}>—</span>;
    return (
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        style={{
          color: '#4F46E5',
          fontFamily: 'monospace',
          fontSize: 12,
          textDecoration: 'none',
        }}
      >
        {link}
      </a>
    );
  }
  return LinkCellRenderer;
}

// ─── Payment Links style defaultColDef ────────────────────────────
export const styledDefaultColDef = {
  filter: false,
  sortable: true,
  resizable: true,
  suppressMenu: false,
  menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  cellStyle: {
    paddingLeft: 20,
    paddingRight: 20,
    fontSize: 13,
    color: '#374151',
  },
  headerClass: 'ag-header-cell-custom',
};
